import { DEVICE_LOCALE, deviceT, translatorFor } from '../messages';
import type { T, UiLocale } from '../messages';
import type { ConflictingAppointment } from '../wire';
import type { OutboxEntry } from './entry';

/**
 * A rejected intent, turned into something the owner can act on (spec §8.3).
 *
 * The spec writes the sentence out, and it is worth reading twice for what it
 * contains:
 *
 * > *"This 2 PM was booked by the concierge while you were offline. Reschedule
 * > Dana?"*
 *
 * Three facts, none of which a generic error message has: **when** the collision
 * was (2 PM), **who took it** (the concierge — that is `conflicts[].source ===
 * 'partner'`), and **who is left without an appointment** (Dana, from the intent
 * the device queued). `enrichConflict` in `core-api` exists to supply the middle
 * one; the outbox's `describe` snapshot supplies the third. This file is the only
 * place the three meet, so the copy is one thing to get right rather than one per
 * view.
 *
 * The honesty rule: when a fact is missing, the sentence loses that clause
 * rather than inventing it. "Someone else has this time" is a worse sentence
 * than the spec's and a better one than a guess. **That rule survives
 * translation** because each opening is one catalog entry that branches on the
 * missing fact itself — not a frame with a pre-built tail spliced into it, which
 * would have forced Spanish to put "for Marisol Vega" where English puts it.
 *
 * What is **not** translated: `rejection.message`. It is the server's own
 * sentence about this exact intent (Domain C), and the catalog's job is only to
 * add the clause that turns a status code into a next step.
 */

export interface OwnerTask {
  /** The rejected outbox entry's id. */
  id: string;
  kind: OutboxEntry['kind'];
  createdAt: string;
  /** One line, the heading of the task card. */
  title: string;
  /** The sentence. Ends in a question when there is something to decide. */
  message: string;
  /** What the collision was, when the server told us. */
  conflicts: ConflictingAppointment[];
  /** True when "Reschedule" is a meaningful button. */
  canReschedule: boolean;
  /** Pre-fills the create flow when the owner taps Reschedule. */
  prefill?: ReschedulePrefill | undefined;
}

export interface ReschedulePrefill {
  serviceId?: string | undefined;
  resourceId?: string | undefined;
  clientId?: string | undefined;
  guestName?: string | undefined;
  startIso?: string | undefined;
  /**
   * **What she had already chosen** (migration 0048).
   *
   * "Find another time" books anew rather than moving anything, so without
   * these the owner would be handed the default length and none of the add-ons
   * she ticked — for a guest who has already been told a price. The rejected
   * intent's own payload is the only record of that ask, which is why they are
   * read from there rather than from an appointment that was never written.
   */
  optionId?: string | undefined;
  addOnItemIds?: readonly string[] | undefined;
}

export interface TaskCopyOptions {
  timezone: string;
  /**
   * The staff member's words. Defaults to the device's, so the two suites that
   * call this outside React (`outbox.test.ts`) need say nothing about language.
   */
  t?: T | undefined;
  /**
   * The same language, for the `Intl` clock in the sentence. `Tasks.tsx` passes
   * both out of one context, which is what keeps them from disagreeing.
   */
  locale?: UiLocale | undefined;
}

export function taskFor(entry: OutboxEntry, options: TaskCopyOptions): OwnerTask {
  const t = options.t ?? deviceT;
  const rejection = entry.rejection;
  const conflicts = rejection?.conflicts ?? [];
  const base = {
    id: entry.id,
    kind: entry.kind,
    createdAt: entry.createdAt,
    conflicts,
  };

  /**
   * `describe.what` is read out of the queued intent, and it is **frozen in the
   * language it was written in** — see `actions.ts`. So the title wraps a
   * snapshot rather than re-deriving one.
   */
  const title = t('tasks.didNotGoThrough', { what: entry.describe.what });

  if (rejection?.code === 'conflict' && rejection.detail?.['reason'] === 'slot_taken') {
    return {
      ...base,
      title,
      message: slotTakenMessage(entry, conflicts, options, t),
      canReschedule: entry.kind === 'appointment.create' || entry.kind === 'appointment.reschedule',
      prefill: prefillFrom(entry),
    };
  }

  return {
    ...base,
    title,
    message: plainMessage(entry, t),
    canReschedule: false,
  };
}

/**
 * The sentence, built from what is actually known: a subject (the clock time,
 * or "that time" when there is none), an opening that names who took it, and a
 * question naming who is left — dropped when the intent carried no person.
 */
function slotTakenMessage(
  entry: OutboxEntry,
  conflicts: ConflictingAppointment[],
  options: TaskCopyOptions,
  t: T,
): string {
  const when = formatTime(
    entry.describe.whenIso,
    options.timezone,
    options.locale ?? DEVICE_LOCALE,
  );
  const winner = conflicts[0];
  const subject = when === null ? t('task.subjectThatTime') : t('task.subjectThisTime', { when });

  const opening = openingFor(subject, winner, t);

  // The question is a second sentence in both languages, which is why joining
  // it with a space is a join rather than the fragment assembly this catalog
  // otherwise refuses: each half stands on its own and each is one entry.
  const person = entry.describe.person;
  if (person === undefined) return `${opening} ${t('task.askPickAnother')}`;
  return entry.kind === 'appointment.create'
    ? `${opening} ${t('task.askRebook', { person })}`
    : `${opening} ${t('task.askReschedule', { person })}`;
}

/**
 * `source` decides the sentence and nothing else does.
 *
 * A booking the partner's agent sold and a booking the owner made on the other
 * iPad are the same 409 and very different news, and the spec's own argument for
 * the whole model — *"the alternative is a device that can silently create a
 * booking the partner has already sold"* — is only visible to the owner if the
 * task says which happened.
 *
 * `who` is passed through as `string | null` rather than pre-formatted, so each
 * language decides where the name goes and whether the clause exists at all.
 */
function openingFor(subject: string, conflict: ConflictingAppointment | undefined, t: T): string {
  if (conflict === undefined) return t('task.takenBySomeone', { subject });
  const who = conflict.client_name;
  switch (conflict.source) {
    case 'partner':
      return t('task.takenByPartner', { subject, who });
    case 'walk_in':
      return t('task.takenByWalkIn', { subject, who });
    case 'import':
      return t('task.takenByImport', { subject, who });
    case 'guest_web':
      return t('task.takenByGuestWeb', { subject, who });
    case 'staff':
      return t('task.takenByStaff', { subject, who });
  }
}

function plainMessage(entry: OutboxEntry, t: T): string {
  const rejection = entry.rejection;
  if (rejection === undefined) return t('task.plainUnknown');
  // `rejection.message` is the server's sentence, printed exactly as it came.
  const message = rejection.message;
  switch (rejection.code) {
    case 'not_found':
      return t('task.plainNotFound', { message });
    case 'forbidden':
      return t('task.plainForbidden', { message });
    case 'conflict':
      return message;
    case 'unsupported':
      return t('task.plainUnsupported', { message });
    default:
      return message;
  }
}

function prefillFrom(entry: OutboxEntry): ReschedulePrefill {
  const payload = entry.payload;
  return {
    serviceId: stringOrUndefined(payload['service_id']),
    resourceId: stringOrUndefined(payload['resource_id']),
    clientId: stringOrUndefined(payload['client_id']) ?? entry.target['client_id'],
    guestName: entry.describe.person,
    startIso: entry.describe.whenIso,
    optionId: stringOrUndefined(payload['option_id']),
    addOnItemIds: stringsOrUndefined(payload['add_on_item_ids']),
  };
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * A queued payload is `Record<string, unknown>` read back out of IndexedDB, so
 * an array of ids is checked element by element rather than cast — a row an
 * older build wrote, or a hand-edited one, must not put a `null` where a
 * service id goes.
 */
function stringsOrUndefined(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const ids = value.filter((v): v is string => typeof v === 'string');
  return ids.length === 0 ? undefined : ids;
}

/**
 * "2 PM", "2:30 PM" — the spec's own phrasing, in the tenant's timezone.
 *
 * `hour12: true` in every language, for the reason `formatClock` states: a door
 * in the USVI says *two o'clock* whichever language is asking. The parts are
 * assembled rather than formatted whole because the sentence wants a bare hour
 * when the minutes are `00`, which no `Intl` option produces — and the **casing
 * of the marker is the catalog's**, not this function's, because English writes
 * `PM` and Spanish writes `p. m.` (see `time.clockOnHour` in `messages/en.ts`).
 */
export function formatTime(
  iso: string | undefined,
  timezone: string,
  locale: UiLocale = DEVICE_LOCALE,
): string | null {
  if (iso === undefined) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(at);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  const t = translatorFor(locale);
  const hour = get('hour');
  const minute = get('minute');
  const meridiem = get('dayPeriod');
  return minute === '00'
    ? t('time.clockOnHour', { hour, meridiem })
    : t('time.clockWithMinutes', { hour, minute, meridiem });
}
